// Usage watch (throwaway: backend/src/modules/usage/README.md). Super admins only; not in the navigation.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Panel, PanelHeader, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Pill from '../components/ui/Pill';
import { KpiBand } from '../components/ui/Kpi';
import { EmptyState, SkeletonRows, ErrorNotice, InfoNotice } from '../components/ui/States';
import { dateShort, duration, fmt, relative } from '../lib/format';
import type { DataResponse, UsageDriveDecisionRequest } from '../contracts';

interface OperatorUsage {
  operator: { id: string; email: string; role: string; isActive: boolean; lastLoginAt: string | null };
  lastSeenAt: string | null;
  activeDays: number;
  sittings: number;
  minutesOnPages: number;
  pageViews: number;
  changes: number;
  failedRequests: number;
  pages: { page: string; views: number; ms: number; last: string }[];
  pagesNeverOpened: string[];
  changesByKind: { route: string; method: string; count: number; last: string }[];
  searches: { at: string; where: string; text: string }[];
  days: string[];
}
interface Summary {
  days: number;
  retentionDays: number;
  operators: OperatorUsage[];
}
interface ActivityRow {
  at: string;
  kind: 'request' | 'view';
  what: string;
  status: number | null;
  ms: number | null;
  title: string | null;
}
interface DriveFile {
  id: string;
  path: string;
  modifiedAt: string;
  size: number | null;
  kind: string;
  modifiedBy: string | null;
  changes: number;
  state: 'open' | 'imported' | 'ignored';
  note: string | null;
  importer: { key: string; label: string; command: string } | null;
}
interface Drive {
  lastScanAt: string | null;
  counts: Record<string, number>;
  files: DriveFile[];
}

const th = 'py-2 pr-4 font-semibold';
const td = 'py-[9px] pr-4 align-top';
const stamp = (v: string) =>
  new Date(v).toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** `POST /api/events/:id/hotel/settings` → "events · hotel · settings": enough to tell what was changed. */
function changeLabel(method: string, route: string) {
  const parts = route
    .replace(/^\/api\//, '')
    .split('/')
    .filter((p) => p && !p.startsWith(':'));
  const verb = method === 'DELETE' ? 'removed' : method === 'POST' ? 'did' : 'edited';
  return `${parts.join(' · ')} (${verb})`;
}

function Operator({ op, days }: { op: OperatorUsage; days: number }) {
  const [showAll, setShowAll] = useState(false);
  const timeline = useQuery<ActivityRow[]>(
    ['usage-activity', op.operator.id, days],
    async () =>
      (await api.get<DataResponse<ActivityRow[]>>(`/usage/operators/${op.operator.id}/activity`, { params: { days } }))
        .data.data,
    { enabled: showAll },
  );
  return (
    <>
      <KpiBand
        cells={[
          { label: 'last signed in', value: op.operator.lastLoginAt ? relative(op.operator.lastLoginAt) : 'never' },
          { label: `days active of ${days}`, value: fmt(op.activeDays) },
          { label: 'sittings', value: fmt(op.sittings), note: 'gaps over 30 min start a new one' },
          { label: 'minutes on pages', value: fmt(op.minutesOnPages) },
          { label: 'changes made', value: fmt(op.changes) },
        ]}
      />
      {op.failedRequests ? (
        <p className="text-table text-ink-2 mb-4">
          {fmt(op.failedRequests)} request(s) were refused or failed. The timeline below shows which.
        </p>
      ) : null}

      <Panel>
        <PanelHeader title="Pages" note={`${op.pages.length} opened`} />
        {op.pages.length ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                <th className={th}>Page</th>
                <th className={`${th} text-right`}>Visits</th>
                <th className={`${th} text-right`}>Time</th>
                <th className={th}>Last opened</th>
              </tr>
            </thead>
            <tbody>
              {op.pages.map((p) => (
                <tr key={p.page} className="border-b border-line text-table">
                  <td className={td}>{p.page}</td>
                  <td className={`${td} text-right tabular`}>{fmt(p.views)}</td>
                  <td className={`${td} text-right tabular`}>{duration(p.ms)}</td>
                  <td className={`${td} text-ink-2`}>{stamp(p.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState title="No pages opened">Nothing in the last {days} days.</EmptyState>
        )}
        {op.pagesNeverOpened.length ? (
          <p className="text-table text-ink-2 mt-3">
            <span className="font-semibold text-ink">Never opened:</span> {op.pagesNeverOpened.join(' · ')}
          </p>
        ) : null}
      </Panel>

      <Panel>
        <PanelHeader title="What he changed" note={`${fmt(op.changes)} changes`} />
        {op.changesByKind.length ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                <th className={th}>Change</th>
                <th className={`${th} text-right`}>Times</th>
                <th className={th}>Last</th>
              </tr>
            </thead>
            <tbody>
              {op.changesByKind.map((c) => (
                <tr key={`${c.method} ${c.route}`} className="border-b border-line text-table">
                  <td className={td}>
                    {changeLabel(c.method, c.route)}
                    <div className="text-small text-ink-3 break-all">
                      {c.method} {c.route}
                    </div>
                  </td>
                  <td className={`${td} text-right tabular`}>{fmt(c.count)}</td>
                  <td className={`${td} text-ink-2`}>{stamp(c.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState title="No changes">He hasn’t changed anything in the last {days} days.</EmptyState>
        )}
      </Panel>

      <Panel>
        <PanelHeader title="Searches" note="newest first" />
        {op.searches.length ? (
          <ul className="text-table divide-y divide-line border-y border-line">
            {op.searches.map((s, i) => (
              <li key={i} className="py-2 flex gap-4 max-md:flex-col max-md:gap-0">
                <span className="text-ink-2 w-[150px] shrink-0 tabular">{stamp(s.at)}</span>
                <span className="flex-1 break-words">“{s.text}”</span>
                <span className="text-ink-3">{s.where}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No searches">None in the last {days} days.</EmptyState>
        )}
      </Panel>

      <Panel>
        <PanelHeader title="Timeline" note="every page and request, newest first, at most 500">
          <Button size="sm" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Hide' : 'Show timeline'}
          </Button>
        </PanelHeader>
        {showAll ? (
          timeline.isLoading ? (
            <SkeletonRows rows={6} />
          ) : timeline.error ? (
            <ErrorNotice>Could not load the timeline.</ErrorNotice>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                  <th className={th}>When</th>
                  <th className={th}>What</th>
                  <th className={`${th} text-right`}>Result</th>
                </tr>
              </thead>
              <tbody>
                {(timeline.data || []).map((r, i) => (
                  <tr key={i} className="border-b border-line text-table">
                    <td className={`${td} whitespace-nowrap text-ink-2 tabular`}>{stamp(r.at)}</td>
                    <td className={`${td} break-all`}>
                      {r.kind === 'view' ? <span className="font-semibold">Viewed {r.what}</span> : r.what}
                    </td>
                    <td className={`${td} text-right tabular text-ink-2`}>
                      {r.kind === 'view' ? duration(r.ms) : r.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : null}
      </Panel>
    </>
  );
}

function DriveSection() {
  const qc = useQueryClient();
  const [state, setState] = useState<'open' | 'imported' | 'ignored' | 'all'>('open');
  const drive = useQuery<Drive>(
    ['usage-drive', state],
    async () => (await api.get<DataResponse<Drive>>('/usage/drive', { params: { state } })).data.data,
  );
  const decide = useMutation(
    async ({ id, ...body }: UsageDriveDecisionRequest & { id: string }) => api.post(`/usage/drive/${id}`, body),
    { onSuccess: () => qc.invalidateQueries('usage-drive') },
  );
  const counts = drive.data?.counts || {};
  return (
    <Panel>
      <PanelHeader
        title="Shared drive"
        note={drive.data?.lastScanAt ? `last scan ${relative(drive.data.lastScanAt)}` : 'not scanned yet'}
      >
        <div className="flex gap-2 flex-wrap">
          {(['open', 'imported', 'ignored', 'all'] as const).map((s) => (
            <Button key={s} size="sm" variant={state === s ? 'primary' : 'secondary'} onClick={() => setState(s)}>
              {s === 'open' ? 'To review' : s[0].toUpperCase() + s.slice(1)}
              {s !== 'all' && counts[s] ? ` (${fmt(counts[s])})` : ''}
            </Button>
          ))}
        </div>
      </PanelHeader>
      <InfoNotice>
        Files changed on the share since the last scan. Run the scan on the Mac that mounts the share:{' '}
        <code className="break-all">
          npm run usage:drive-scan -- --share={'"/Volumes/…/DIVERT Mental Health"'} --api=https://&lt;platform&gt;
        </code>{' '}
        with <code>PLATFORM_TOKEN</code> set. Only names, dates, sizes and the Office “last modified by” name are sent.
        Bring data over with the importer shown (each starts as a dry run), then mark the file imported. A file changed
        again later comes back to this list.
      </InfoNotice>
      {drive.isLoading ? (
        <SkeletonRows rows={5} />
      ) : drive.error ? (
        <ErrorNotice>Could not load the shared-drive changes.</ErrorNotice>
      ) : !drive.data?.files.length ? (
        <EmptyState title="Nothing here">
          No files {state === 'open' ? 'waiting for review' : `marked ${state}`}.
        </EmptyState>
      ) : (
        <table className="w-full mt-3 max-md:block">
          <thead className="max-md:hidden">
            <tr className="text-left text-small text-ink-2 border-b border-line-strong">
              <th className={th}>File</th>
              <th className={th}>Changed</th>
              <th className={th}>Where its data goes</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody className="max-md:block">
            {drive.data.files.map((f) => (
              <tr key={f.id} className="border-b border-line text-table max-md:block max-md:py-2">
                <td className={`${td} max-md:block max-md:py-1 max-md:pr-0 max-md:text-left break-words`}>
                  {f.path.split('/').pop()}
                  <div className="text-small text-ink-3 break-all">{f.path}</div>
                </td>
                <td className={`${td} max-md:block max-md:py-1 max-md:pr-0 max-md:text-left whitespace-nowrap`}>
                  {dateShort(f.modifiedAt)}
                  <div className="text-small text-ink-2">{f.modifiedBy || 'name not stored'}</div>
                  {f.changes > 1 ? <div className="text-small text-ink-3">changed {f.changes} times</div> : null}
                </td>
                <td className={`${td} max-md:block max-md:py-1 max-md:pr-0`}>
                  {f.importer ? (
                    <>
                      <div>{f.importer.label}</div>
                      <code className="text-small text-ink-2 break-all block mt-1">{f.importer.command}</code>
                    </>
                  ) : (
                    <span className="text-ink-2">No importer yet ({f.kind})</span>
                  )}
                </td>
                <td
                  className={`${td} max-md:block max-md:py-1 max-md:pr-0 max-md:text-left whitespace-nowrap text-right`}
                >
                  {f.state === 'open' ? (
                    <div className="flex gap-2 justify-end max-md:justify-start">
                      <Button size="sm" onClick={() => decide.mutate({ id: f.id, state: 'imported' })}>
                        Imported
                      </Button>
                      <Button size="sm" variant="quiet" onClick={() => decide.mutate({ id: f.id, state: 'ignored' })}>
                        Ignore
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end items-center max-md:justify-start">
                      <Pill tone={f.state === 'imported' ? 'done' : 'inactive'}>{f.state}</Pill>
                      <Button size="sm" variant="quiet" onClick={() => decide.mutate({ id: f.id, state: 'open' })}>
                        Reopen
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export default function Usage() {
  const [days, setDays] = useState(30);
  const [picked, setPicked] = useState<string | null>(null);
  const summary = useQuery<Summary>(
    ['usage-summary', days],
    async () => (await api.get<DataResponse<Summary>>('/usage/summary', { params: { days } })).data.data,
  );
  const ops = useMemo(() => summary.data?.operators || [], [summary.data]);
  // Default: the most active operator who isn't a super admin.
  const fallback = useMemo(
    () =>
      [...ops]
        .filter((o) => o.operator.role !== 'super_admin' && o.operator.isActive)
        .sort((a, b) => b.pageViews + b.changes - (a.pageViews + a.changes))[0]?.operator.id ?? ops[0]?.operator.id,
    [ops],
  );
  const op = ops.find((o) => o.operator.id === (picked ?? fallback));

  return (
    <div>
      <PageHeader
        title="Usage watch"
        sub={`Who uses the platform, for what, and what still changes on the shared drive. Only super admins see this page. Records are kept ${summary.data?.retentionDays ?? 90} days.`}
        aside={
          <div className="flex gap-3 flex-wrap">
            <label className="text-table">
              <span className="sr-only">Operator</span>
              <select
                className="border border-line-strong bg-white px-3 py-[9px] text-table"
                value={op?.operator.id || ''}
                onChange={(e) => setPicked(e.target.value)}
              >
                {ops.map((o) => (
                  <option key={o.operator.id} value={o.operator.id}>
                    {o.operator.email}
                    {o.operator.isActive ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-table">
              <span className="sr-only">Period</span>
              <select
                className="border border-line-strong bg-white px-3 py-[9px] text-table"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </label>
          </div>
        }
      />
      {summary.isLoading ? (
        <SkeletonRows rows={6} />
      ) : summary.error ? (
        <ErrorNotice>Could not load usage. This page is for super admins only.</ErrorNotice>
      ) : op ? (
        <Operator key={`${op.operator.id}-${days}`} op={op} days={days} />
      ) : (
        <EmptyState title="No operators">There are no operator accounts.</EmptyState>
      )}
      <DriveSection />
    </div>
  );
}
